import { Application } from '../application/application';
import { ContainerDefinition, Container, DEFAULT_DRAWER_WIDTH, REQUEST_ATTRIB_DOMAIN } from './cabinets';
import { ContainerPanel } from './panel';
import { createObservationContainerDefinition } from '../observation/container-observation';
import { createModelingContainerDefinition } from '../modeling/container-modeling';
import { eventObserver } from '../dom/dom';
import { DialogDefinition } from './dialog';
import { createExportDialogDefinition } from '../system/dialog-export';
import { createReportsDialogDefinition } from '../system/dialog-reports';
import { TopMenu } from '@mapp/r-mapp-lib';

export enum LauncherGroup {
	sensor = 'sensor',
	observation = 'observation',
	modelling = 'modelling',
	visual = 'visual',
	nonevisual = 'nonevisual'
}

export interface LauncherDefinition {
	name: string,
	category: string, //allways inspection for launchers used here
	label: string,
	icon: string,
	group: LauncherGroup,
	factory: string,
	permission: string,
	autoStart?: boolean,
	options: object,
	//legacy property, used to display dashboard launchers in the inspection panel
	//TODO remove when dashboard is history
	handler?: any
}


export let topContainerDefinition: ContainerDefinition = {
	build: (panel: ContainerPanel, containerId: string, application: Application): Container => {
		return new TopContainer(panel, containerId, application);
	},
	url: '/' + REQUEST_ATTRIB_DOMAIN + '/template/system/container-top',
	cabinets: [
		{ name: 'cab0', drawers: [{ name: 'launchers', width: DEFAULT_DRAWER_WIDTH }] }
	]
}

const containerDefinitionFactories: { [key: string]: () => ContainerDefinition } = {
	observation: createObservationContainerDefinition,
	modeling: createModelingContainerDefinition
}

const dialogDefinitionFactories: { [key: string]: () => DialogDefinition } = {
	export: createExportDialogDefinition,
	reports: createReportsDialogDefinition
}

export class TopContainer extends Container {
	private launcherDefinitions: LauncherDefinition[];
	private topMenuComponent: TopMenu; //a svelte component

	constructor(panel: ContainerPanel, containerId: string, app: Application) {
		super(panel, containerId, app);

		this.launcherDefinitions = [];

		//legacy: needed for as long we got windows 20211202 START		
		app.getWindowManager2().getObservable('tlmd-window-opened').subscribe(ev => {
			let windowId = ev[1];
			this.launcherActivated(windowId);
		});
		app.getWindowManager2().getObservable('tlmd-window-closed').subscribe(ev => {
			let windowId = ev[1];
			this.launcherDeactivated(windowId);
		});
		//legacy: needed for as long we got windows 20211202 END		


		this.addContainerDelegatedSubscription('click', 'div.launcher', evAndTrgt => {
			evAndTrgt.event.preventDefault();
			//legacy dashboard launchers also have their name in the list of classes and need to be treated differently
			//TODO remove when dashboard is history
			if (evAndTrgt.target.classList.contains('launcher') && evAndTrgt.target.classList.contains('nohandler')) {
				//proper inspection panel launcher
				let containerId = evAndTrgt.target.dataset.name;
				if (containerId.startsWith('dlg-')) {
					//a dialog has been launched
					this.getPanel().switchDialog(containerId)
						.then((_) => this.launcherActivated(containerId)) //evAndTrgt.target.firstElementChild.classList.add('active') )
						.catch((_) => this.launcherDeactivated(containerId)); //evAndTrgt.target.firstElementChild.classList.remove('active') );
				} else {
					//an inspector container has been launched
					this.getPanel().switchContainer(containerId)
						.then((_) => this.launcherActivated(containerId)) //evAndTrgt.target.firstElementChild.classList.add('active') )
						.catch((_) => this.launcherDeactivated(containerId)); //evAndTrgt.target.firstElementChild.classList.remove('active') );
				}
			} else {
				//legacy dashboard launcher
				//TODO remove when dashboard is history
			}
		});

	}

	public containerClosed(containerId: string): void {
		this.launcherDeactivated(containerId);
	}

	public dialogClosed(dialogId: string): void {
		this.launcherDeactivated(dialogId);
	}

	protected launcherActivated(launcherName: string): void {
		let launcher = <HTMLDivElement>this.getContainerNode().querySelector(`div.launcher[data-name='${launcherName}']`);
		if (launcher) {
			launcher.firstElementChild.classList.add('active');
		}
	}

	protected launcherDeactivated(launcherName: string): void {
		let launcher = <HTMLDivElement>this.getContainerNode().querySelector(`div.launcher[data-name='${launcherName}']`);
		if (launcher) {
			launcher.firstElementChild.classList.remove('active');
		}
	}

	public reset(): TopContainer {
		if (this.launcherDefinitions) {
			this.launcherDefinitions.forEach((ld: LauncherDefinition) => {
				this.getPanel().removeContainer(ld.name);
			});
			this.launcherDefinitions = [];
		}

		return this;
	}

	public setLaunchers(launcherDefinitions: LauncherDefinition[]): TopContainer {

		const launchersToStart: LauncherDefinition[] = [];

		const curLauncherCount = this.launcherDefinitions.length;
		let removeCount = 0;
		let addCount = 0;
		this.launcherDefinitions
			.filter((ld: LauncherDefinition) => launcherDefinitions.findIndex((newld: LauncherDefinition) => newld.name === ld.name) === -1)
			.forEach((ld: LauncherDefinition) => {
				removeCount++;
				if (containerDefinitionFactories[ld.factory] !== undefined) {
					console.log(`Removing container ${ld.name}`);
					this.getPanel().removeContainer(ld.name);
				}
				if (dialogDefinitionFactories[ld.factory] !== undefined) {
					console.log(`Removing dialog ${ld.name}`);
					this.getPanel().removeDialog(ld.name);
				}
			});

		this.launcherDefinitions = this.launcherDefinitions
			.filter((ld: LauncherDefinition) => launcherDefinitions.findIndex((newld: LauncherDefinition) => newld.name === ld.name) !== -1);


		launcherDefinitions
			.filter((ld: LauncherDefinition) => this.launcherDefinitions.findIndex((oldld: LauncherDefinition) => oldld.name === ld.name) === -1)
			.forEach((ld: LauncherDefinition) => {
				addCount++;
				if (containerDefinitionFactories[ld.factory] !== undefined) {
					console.log(`Adding container ${ld.name}`);
					this.getPanel().addContainer(ld.name, ld.factory, containerDefinitionFactories[ld.factory]());
				}
				if (dialogDefinitionFactories[ld.factory] !== undefined) {
					console.log(`Adding dialog ${ld.name}`);
					this.getPanel().addDialog(ld.name, ld.factory, dialogDefinitionFactories[ld.factory]());
				}
				this.launcherDefinitions.push(ld);

				if (ld.autoStart === true) {
					launchersToStart.push(ld);
				}
			});

		if (removeCount !== 0 || addCount !== 0) {
			console.log(`Launchers updated: added ${addCount}, removed ${removeCount}, total: ${this.launcherDefinitions.length} (was ${curLauncherCount})`);
			if (this.getContainerNode().firstElementChild) {
				console.log('DrawLaunchers from setLaunchers');
				this.drawLaunchers();
			}
		}

		window.setTimeout(() => {
			launchersToStart.forEach((ld: LauncherDefinition) => {
				console.log(`Auto starting ${ld.name}.`);
				(<HTMLAnchorElement>document.querySelector(`a.tlmd-launcher.${ld.name}`))?.click();
			});
		}, 1000);


		return this;
	}

	//	/**
	//	 * This one gets called once the skeleton markup is available
	//	 */
	//	protected finishContainerBuild(): Promise<Container> {
	//		return new Promise<Container>((resolve, _reject) => {
	//			if (this.launcherDefinitions !== undefined) {
	//				console.log('DrawLaunchers from finishContainerBuild promise');
	//				this.drawLaunchers();
	//			}
	//			resolve(this)
	//		});
	//		}

	/**
	 * This one gets called once the skeleton markup is available
	 */
	protected finishContainerBuild(): Promise<Container> {
		const self = this;
		return new Promise<Container>((resolve, _reject) => {
			const svelteContainer = <HTMLElement>this.getDrawer(0, 0).getContent().querySelector('div.tlmd-sections');
			//console.log(svelteContainer);
			const language = this.getApplication().getLanguage();
			self.topMenuComponent = new TopMenu({
				target: svelteContainer,
				props: {
					lang:  language,
					launchers: this.launcherDefinitions
				}
			});
			resolve(this)
		});
	}

	public drawLaunchers(): void {
		this.topMenuComponent.$$set({
			launchers: this.launcherDefinitions
		});
	}

	//	public drawLaunchers(): void {
	//		let launcherDrawer = this.getDrawer(0, 0);
	//		let sensorSection: HTMLElement = launcherDrawer.getContent().querySelector('div.tlmd-launcher-group.sensor');
	//		let observationSection: HTMLElement = launcherDrawer.getContent().querySelector('div.tlmd-launcher-group.observation');
	//		let modellingSection: HTMLElement = launcherDrawer.getContent().querySelector('div.tlmd-launcher-group.modelling');
	//		let visualSection: HTMLElement = launcherDrawer.getContent().querySelector('div.tlmd-launcher-group.visual');
	//		let nonevisualSection: HTMLElement = launcherDrawer.getContent().querySelector('div.tlmd-launcher-group.nonevisual');
	//
	//		sensorSection.innerHTML = this.getLaunchersMarkup(LauncherGroup.sensor);
	//		observationSection.innerHTML = this.getLaunchersMarkup(LauncherGroup.observation);
	//		modellingSection.innerHTML = this.getLaunchersMarkup(LauncherGroup.modelling);
	//		visualSection.innerHTML = this.getLaunchersMarkup(LauncherGroup.visual);
	//		nonevisualSection.innerHTML = this.getLaunchersMarkup(LauncherGroup.nonevisual);
	//	}
	//
	//	private getLaunchersMarkup(group: LauncherGroup): string {
	//		let markup = this.launcherDefinitions
	//			.filter(ld => ld.group === group)
	//			.map(ld => `<div class="launcher${ld.handler ? ' ' + ld.name : ''}" data-name="${ld.name}"><a class="btn ${ld.name} tlmd-launcher" href="#"><span class="${ld.icon}"></span></a><label>${ld.label}</label></div>`)
	//			.join('');
	//		return markup;
	//	}
  /**
   * Override this method to do special processing when we just came into view. 
   */
   
   /*
   The Top Container is a bit special. It gets fully populated while the inspector panel has it's display
   style set to none. Taking element  dimensions will result 0 and our TopMenu svelte component will 
   break. By calling this function, the measuremnts are performed again with now correct dimensions.
    */
  public onFocus(): void {
	// this triggerrs the svelte component to re-calculate dimensions since all are 0 upon initialiazation (parent display = none)
			this.topMenuComponent.$$set({
			launchers: this.launcherDefinitions
		});

  }

}
